import { Component, Input, OnInit } from '@angular/core';
import { FromToDetail, ReportImage } from '../../../../../_objects/report-element';
import { FileSystemAccessService } from '../../../../../_services/file-system-access.service';
import { MousePoint, Notifier, OfflineReportPreviewService } from '../../../../../_services/offline-report-preview.service';
import { FILE_SYSTEM } from '../../../../electron.constants';

@Component({
    selector: 'app-report-image-container-offline',
    templateUrl: './report-image.component.html',
    styleUrls: ['./report-image.component.scss'],
    standalone: false
})
export class ReportImageOfflineComponent implements OnInit {

  @Input() public pageKey: string;
  @Input() public imageKey: string;
  @Input() public elementLocation: string;

  public reportImage: ReportImage;
  private resizeMarkerSize: number;
  private resizing: boolean;

  private notifier: Notifier;

  private blobPath: string;

  readonly reportImageStyle = 'position: absolute; width: 100%; height: 100%;';
  readonly imageSrcStyle = 'margin: 0; white-space: pre-wrap; height: auto;';

  constructor(
    public designService: OfflineReportPreviewService,
    public fileSysAccessService: FileSystemAccessService
  ) {
    this.resizeMarkerSize = 36;
    this.resizing = false;
  }

  public ngOnInit() {
    this.notifier = {
      componentName: 'report-image.component',
      ids: { pageId: this.pageKey, elementId: this.imageKey }
    };
    this.reportImage = this.designService.getImage(this.imageKey);
    this.fileSysAccessService.getSaveBlobFolderPath()
      .then(blobPath => this.blobPath = blobPath);
  }

  public onClick(event: MouseEvent) {
    if (this.reportImage.isSelectable) {
      event.stopPropagation();
    }
  }

  public getImageUrl(fileName: string): string {
    if (fileName) {
      const filePath = this.designService.getImageUrl(fileName.split('/').pop());
      return FILE_SYSTEM.PATH.FILE_PREFIX
        + this.blobPath
        + this.genFilePath(filePath);
    } else {
      return '';
    }
  }

  public onDragStart(event: DragEvent) {
    // console.log('ReportImage onDragStart');
    event.dataTransfer.setData('text', this.imageKey);
    event.dataTransfer.effectAllowed = 'move';

    const fromDetail: FromToDetail = {
      location: this.reportImage.getLocation(),
      pageId: this.pageKey
    };
    const dragPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };
  }

  public onDragOver(event: DragEvent) {
    // console.log('ReportImage onDragOver');
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    event.stopPropagation();
  }

  public onDrop(event: DragEvent) {
    // console.log('ReportImage onDrop');
    event.preventDefault();
    const template  = { ...this.designService.originTemplate };
    template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
    const dragElemId = event.dataTransfer.getData('text');

    const toDetail: FromToDetail = {
      location: this.reportImage.getLocation(),
      pageId: this.pageKey
    };
    const dropPoint: MousePoint = {
      onElementX: this.reportImage.getLeftPx() + event.offsetX,
      onElementY: this.reportImage.getTopPx() + event.offsetY
    };

    event.dataTransfer.clearData('text');
    event.stopPropagation();
  }

  public getResizeMarkerSize(): string {
    return this.resizeMarkerSize + 'px';
  }

  public getResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportImage.getWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportImage.getWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportImage.getHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportImage.getHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public onResizing(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  public onResizeMarkerUp(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }

  public onResizeMarkerDown(event: MouseEvent, vertical: string, horizontal: string) {
    event.preventDefault();
    this.resizing = true;
    const beginPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };
    event.stopPropagation();
  }

  public onResizeMarkerLeave(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }

  private genFilePath(filePath: string) {
    const pathSeparator = this.fileSysAccessService.getSystemPathSeparator();
    const filePathParts = filePath.split('/');
    return filePathParts.join(pathSeparator);
  }

  private genCacheParam() {
    return '?var=' + performance.now();
  }
}
